import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import Marchio from '../components/Marchio'
import {
  Card,
  EmptyState,
  ErrorBanner,
  Spinner,
  StatusBadge,
  timeAgo,
} from '../components/common'
import type { AgentInstaller, Capability, Probe, ProbeGroup } from '../api/types'
import { DataTable, type Column } from '../components/DataTable'
import FleetVersions from '../components/FleetVersions'
import { colonnaTag, useTagDi } from '../components/Tag'
import { useAsync, usePolling } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { VarianteSenzaCurl } from '../components/VarianteSenzaCurl'
import { SbomCenso } from '../components/SbomCenso'

export default function Fleet() {
  const probes = usePolling(() => api.probes(), 10000)
  const groups = usePolling(() => api.groups(), 60000)
  const capabilities = useAsync(() => api.capabilities(), [])
  const [enrolling, setEnrolling] = useState(false)
  const [installer, setInstaller] = useState<AgentInstaller | null>(null)
  const tag = useTagDi('appliance')
  const righe = probes.data ?? []
  const piccolo = { fontSize: 11 }
  const colonne: Column<Probe>[] = [
    {
      key: 'name',
      header: t('Nome'),
      value: (p) => [p.name, p.hostname !== p.name ? p.hostname : '', p.censo_appliance ? t('Censo Appliance') : ''].filter(Boolean).join(' '),
      render: (p) => (
        <>
          <Link to={`/fleet/${p.id}`}>{p.name}</Link>
          {/* L'hostname si mostra solo se aggiunge qualcosa: una
              sonda che non è stata rinominata si chiama già come la
              macchina, e ripeterlo sotto sembra un errore. */}
          {p.hostname && p.hostname !== p.name && (
            <div className="mono" style={{ color: 'var(--text-muted)' }}>
              {p.hostname}
            </div>
          )}
          {p.censo_appliance && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, marginTop: 2 }}
                 title={t('Appliance CensoOS: la sonda di cattura dal filo')}>
              <Marchio size={14} />
              <span>{t('Censo Appliance')}</span>
              {p.temp_summary && <span style={{ color: 'var(--text-muted)' }}>· {p.temp_summary}</span>}
            </div>
          )}
        </>
      ),
    },
    // I tag vengono dal catalogo condiviso: si filtrano e si modificano sul posto.
    ...colonnaTag<Probe>('appliance', tag, (p) => p.id),
    {
      key: 'status',
      header: t('Stato'),
      value: (p) => [p.status, p.enabled ? '' : t('Disattivata'), p.degraded_reason].filter(Boolean).join(' '),
      render: (p) => (
        <>
          <StatusBadge status={p.status} />
          {/* Una sonda disattivata è online e in salute: senza dirlo
              qui, chi cerca perché non riceve job non lo trova. */}
          {!p.enabled && <div style={{ ...piccolo, color: 'var(--warn)' }}>{t('Disattivata')}</div>}
          {p.degraded_reason && <div style={{ ...piccolo, color: 'var(--warn)' }}>{p.degraded_reason}</div>}
        </>
      ),
    },
    {
      key: 'capabilities',
      header: t('Capability operative'),
      value: (p) => p.effective_capabilities.join(' '),
      render: (p) => <CapabilitySummary probe={p} />,
    },
    {
      key: 'capacity',
      header: t('Capacità'),
      value: (p) => (typeof p.metrics?.cores === 'number' ? p.metrics.cores : null),
      render: (p) => <Capacity metrics={p.metrics} />,
    },
    {
      key: 'version',
      header: t('Versione'),
      mono: true,
      render: (p) => (
        <>
          {p.version ?? '—'}
          {p.auto_update && (
            <div style={{ ...piccolo, color: 'var(--text-muted)', fontFamily: 'inherit' }}>
              {t('aggiornamento automatico')}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'last_heartbeat_at',
      header: t('Ultimo heartbeat'),
      type: 'date',
      value: (p) => p.last_heartbeat_at,
      render: (p) => <span title={p.last_heartbeat_at ?? ''}>{timeAgo(p.last_heartbeat_at)}</span>,
    },
  ]
  const colonneGruppi: Column<ProbeGroup>[] = [
    { key: 'name', header: t('Nome') },
    { key: 'mode', header: t('Modalità') },
    { key: 'assignment_policy', header: t('Politica di assegnazione') },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Sonde')}</h1>
          <p className="page__subtitle">{t('Genera un token di registrazione e installa una sonda nella rete da sottoporre a discovery. In ambienti multisede, configura una sonda per sede e raggruppale per consentire l’assegnazione automatica dei job alla sonda disponibile. Verifica periodicamente versione, capability operative e stato; dopo ogni aggiornamento della piattaforma, controlla l’allineamento delle versioni, perché le nuove funzionalità di raccolta richiedono una versione compatibile della sonda.')}</p>
        </div>
        <button
          onClick={() => {
            setInstaller(null)
            setEnrolling((open) => !open)
          }}
        >
          {enrolling ? t('Annulla') : t('Arruola nuova sonda')}
        </button>
      </header>

      <ErrorBanner error={probes.error} />

      {enrolling && !installer && (
        <EnrollForm
          groups={groups.data ?? []}
          capabilities={capabilities.data ?? []}
          onCreated={(created) => setInstaller(created)}
        />
      )}

      {installer && (
        <InstallerCard
          installer={installer}
          onDone={() => {
            setInstaller(null)
            setEnrolling(false)
          }}
        />
      )}

      <FleetVersions />

      <Card title={t('Sonde registrate')}>
        {probes.loading && !probes.data ? (
          <Spinner />
        ) : !probes.data?.length ? (
          <EmptyState
            title={t('Nessuna sonda arruolata')}
            hint={t('Genera un token di arruolamento e avvia un collector nella rete da inventariare.')}
          />
        ) : (
          <DataTable rows={righe} columns={colonne} rowKey={(p) => p.id} conteggio={{ nome: t('sonde') }} />
        )}
      </Card>

      {groups.data && groups.data.length > 0 && (
        <Card title={t('Gruppi di sonde')}>
          <DataTable rows={groups.data} columns={colonneGruppi} rowKey={(g) => g.id} conteggio={{ nome: t('gruppi') }} />
        </Card>
      )}

      <SbomCenso prodotti={['probe']} titolo={t('SBOM e VEX della sonda')} />
    </>
  )
}

/**
 * Modulo di arruolamento: nome, gruppo e capability con cui la sonda entra in
 * servizio.
 *
 * La scelta si fa qui e non dopo perché chi arruola sa già a cosa destina la
 * sonda: rimandarla significa che per il tempo che passa fino alla prima
 * modifica la sonda opera su tutto — cioè proprio quando nessuno la sta ancora
 * guardando.
 */
function EnrollForm({
  groups,
  capabilities,
  onCreated,
}: {
  groups: { id: string; name: string }[]
  capabilities: Capability[]
  onCreated: (installer: AgentInstaller) => void
}) {
  const [name, setName] = useState('')
  const [groupId, setGroupId] = useState(groups[0]?.id ?? '')
  const [restrict, setRestrict] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  function toggle(capability: string) {
    setSelected((current) =>
      current.includes(capability)
        ? current.filter((c) => c !== capability)
        : [...current, capability],
    )
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      onCreated(
        await api.createAgentInstaller({
          group_id: groupId || null,
          name: name.trim() || null,
          // Nessuna restrizione = `null`: la lista vuota significherebbe
          // "nessuna capability", cioè una sonda che nasce ferma.
          capabilities: restrict ? selected : null,
        }),
      )
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuova sonda')}>
      <ErrorBanner error={error} />

      <div className="row">
        <div className="field">
          <label htmlFor="probe-name">{t('Nome')}</label>
          <input
            id="probe-name"
            type="text"
            value={name}
            placeholder={t('es. sonda-milano-dc1')}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="field__hint">
            {t('Se lo lasci vuoto la sonda userà il proprio hostname.')}
          </p>
        </div>
        <div className="field">
          <label htmlFor="probe-group">{t('Gruppo')}</label>
          <select id="probe-group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">{t('Nessun gruppo')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="checkbox">
        <input
          id="probe-restrict"
          type="checkbox"
          checked={restrict}
          onChange={(e) => setRestrict(e.target.checked)}
        />
        <label htmlFor="probe-restrict">
          {t('Limita le capability di questa sonda')}
          <small>
            {t('Senza limiti la sonda esegue tutto ciò che il suo binario sa fare. Limitarle serve a dedicarne una a una funzione sola — per esempio una sonda al solo NetFlow.')}
          </small>
        </label>
      </div>

      {restrict && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend className="field__hint" style={{ marginBottom: 8 }}>
            {t('Le capability abilitate qui possono solo restringere ciò che la sonda dichiara di saper fare: non aggiungono funzioni che il suo binario non ha.')}
          </legend>
          {capabilities.map((c) => {
            // Un modulo non coperto dalla licenza non si può arruolare: il
            // backend lo rifiuterebbe, così lo si mostra bloccato con il motivo.
            const locked = c.licensed === false
            return (
              <div className="checkbox" key={c.capability}>
                <input
                  id={`cap-${c.capability}`}
                  type="checkbox"
                  checked={selected.includes(c.capability)}
                  disabled={locked}
                  onChange={() => toggle(c.capability)}
                />
                <label htmlFor={`cap-${c.capability}`}>
                  {t(c.label)} <span className="mono">({c.capability})</span>
                  <small>
                    {locked ? (
                      <span style={{ color: 'var(--warn)' }}>
                        {t('Non compreso nella licenza')}
                      </span>
                    ) : (
                      t(c.hint)
                    )}
                  </small>
                </label>
              </div>
            )
          })}
          {restrict && selected.length === 0 && (
            <div className="banner banner--warn">
              {t('Nessuna capability selezionata: la sonda si arruolerà ma non riceverà alcun job.')}
            </div>
          )}
        </fieldset>
      )}

      <button onClick={submit} disabled={busy}>
        {busy ? t('Generazione…') : t('Genera token di arruolamento')}
      </button>
    </Card>
  )
}

/**
 * Il comando che installa la sonda, e nient'altro da capire.
 *
 * Prima qui c'era il token da copiare, con accanto una riga di comando da
 * completare a mano: indirizzo del Control Plane, immagine da procurarsi,
 * certificato da far considerare fidato. Sei passaggi manuali su una macchina
 * dentro la rete di un cliente, cioè sei modi di sbagliare in un posto a cui non
 * si torna facilmente.
 *
 * Il comando arriva già completo dal Control Plane e **non va composto qui**: è
 * l'unico che sa quale certificato sta presentando, e quindi se serve appuntarne
 * la chiave pubblica. Comporlo nella console significherebbe due posti che
 * decidono la stessa cosa, e prima o poi divergono.
 */
function InstallerCard({
  installer,
  onDone,
}: {
  installer: AgentInstaller
  onDone: () => void
}) {
  const [copied, setCopied] = useState(false)
  // Quale dei due comandi si sta guardando.
  //
  // Una scelta esplicita e non un rilevamento dal browser: chi apre la console
  // da un portatile Windows spesso installa la sonda su un server Linux, e
  // indovinare dal `navigator` mostrerebbe il comando sbagliato con sicurezza —
  // che è la cosa peggiore, perché nessuno rilegge un comando che sembra giusto.
  const [sistema, setSistema] = useState<'linux' | 'windows'>('linux')
  const comando = sistema === 'windows' ? installer.command_windows : installer.command

  async function copy() {
    try {
      await navigator.clipboard.writeText(comando)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // Il clipboard non è disponibile fuori da un contesto sicuro: il comando
      // resta visibile e selezionabile, che è il ripiego giusto.
      setCopied(false)
    }
  }

  function scegli(quale: 'linux' | 'windows') {
    setSistema(quale)
    // «Copiato» si riferiva all'altro comando: lasciarlo acceso farebbe credere
    // di avere negli appunti quello che si sta guardando adesso.
    setCopied(false)
  }

  return (
    <Card title={t('Installa la sonda')}>
      <p style={{ marginTop: 0 }}>
        {t('Esegui questo comando sulla macchina, dentro la rete da inventariare. Scarica la sonda, la installa come servizio e la arruola.')}
      </p>

      <div className="segmented" role="tablist" aria-label={t('Sistema operativo della sonda')}>
        <button
          role="tab"
          aria-selected={sistema === 'linux'}
          className={sistema === 'linux' ? 'segmented__on' : ''}
          onClick={() => scegli('linux')}
        >
          {t('Linux')}
        </button>
        <button
          role="tab"
          aria-selected={sistema === 'windows'}
          className={sistema === 'windows' ? 'segmented__on' : ''}
          onClick={() => scegli('windows')}
        >
          {t('Windows')}
        </button>
      </div>

      <pre className="json">{comando}</pre>
      {sistema === 'windows' && <VarianteSenzaCurl comando={installer.command_windows_legacy} />}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <button onClick={copy}>{copied ? t('Copiato') : t('Copia il comando')}</button>
        <button className="secondary" onClick={onDone}>
          {t('Ho finito')}
        </button>
      </div>

      <p className="field__hint" style={{ marginTop: 12 }}>
        {t('Scade il {quando} ed è per una sonda sola. Finché non è stato usato vale quanto una credenziale: non inoltrarlo su canali che non controlli.', {
          quando: new Date(installer.expires_at).toLocaleString(localeCorrente()),
        })}
      </p>

      {installer.pinned && (
        <p className="field__hint">
          {sistema === 'windows'
            ? t('Il comando contiene l’impronta del certificato di questo server, perché il certificato è ancora autofirmato: è una verifica esatta, non un «fidati di chiunque». Sostituendo il certificato con uno vero il comando tornerà a essere un download normale, e le sonde già installate continueranno a funzionare.')
            : t('Il comando contiene l’impronta della chiave pubblica di questo server, perché il certificato è ancora autofirmato: è una verifica esatta, non un «fidati di chiunque». Sostituendo il certificato con uno vero il comando tornerà a essere un download normale, e le sonde già installate continueranno a funzionare.')}
        </p>
      )}

      {sistema === 'windows' && (
        <p className="field__hint">
          {t('Apri PowerShell come amministratore e incolla le due righe: la prima scarica l’installer in un file ({file}) con curl.exe, che verifica il certificato del server per impronta; la seconda lo esegue. Prima che parta puoi aprire il file e leggerlo: è un normale file su disco, non codice che gira al volo. curl.exe c’è da Windows 10 e da Windows Server 2019; per Windows Server 2016 usa la variante qui sopra.', { file: 'censo-install.ps1' })}
        </p>
      )}

      <p className="field__hint">
        {sistema === 'windows' ? (
          <>
            {t('Serve Windows x64 e i privilegi di amministratore. La sonda si registra come servizio, riparte da sola dopo un riavvio, e non fa la cattura diretta dal filo: quella usa un’interfaccia del kernel Linux. Tutto il resto — SNMP, porte, SSH, WinRM, NetFlow ricevuto, connettori — funziona uguale.')}{' '}
          </>
        ) : (
          <>{t('Serve Linux, curl e i permessi di root.')} </>
        )}
        {t('La sonda parla col Control Plane solo in uscita, su 443: non aprire niente in ingresso.')}{' '}
        {installer.capabilities === null ? (
          <>{t('Opererà su tutte le capability che dichiara.')}</>
        ) : installer.capabilities.length === 0 ? (
          <>
            {t('Nessuna capability abilitata: si arruolerà e resterà ferma finché non ne abiliti almeno una dalla sua scheda.')}
          </>
        ) : (
          <>
            {t('Capability abilitate all’arruolamento:')}{' '}
            {installer.capabilities.map((c) => (
              <span className="chip" key={c}>
                {c}
              </span>
            ))}
          </>
        )}
      </p>
    </Card>
  )
}

/**
 * Capability su cui la sonda riceve davvero job.
 *
 * Mostrare solo quelle dichiarate renderebbe impossibile capire perché una
 * discovery resta in attesa: la capability c'è, ma è disabilitata — oppure è
 * abilitata e la sonda non ce l'ha.
 */
function CapabilitySummary({ probe }: { probe: Probe }) {
  const effective = probe.effective_capabilities
  const suppressed = probe.capabilities.filter((c) => !effective.includes(c))

  return (
    <>
      {effective.length === 0 ? (
        <span className="text-muted">{t('nessuna')}</span>
      ) : (
        effective.map((c) => (
          <span className="chip" key={c}>
            {c}
          </span>
        ))
      )}
      {suppressed.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {t('disabilitate: {elenco}', { elenco: suppressed.join(', ') })}
        </div>
      )}
      {probe.unavailable_capabilities.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--warn)' }}>
          {t('abilitate ma non disponibili: {elenco}', { elenco: probe.unavailable_capabilities.join(', ') })}
        </div>
      )}
    </>
  )
}

/**
 * Capacità di calcolo dichiarata dalla sonda nell'heartbeat.
 *
 * Il parallelismo di una scansione SNMP si dimensiona sui core della macchina
 * che la esegue: senza questo dato, chi assegna gli scope alle sonde lo fa alla
 * cieca, e una sonda lenta perché sottodimensionata è indistinguibile da una
 * lenta perché occupata.
 */
function Capacity({ metrics }: { metrics: Record<string, unknown> }) {
  const cores = typeof metrics?.cores === 'number' ? metrics.cores : null
  if (cores === null) {
    // Le sonde precedenti a questa versione non inviano le metriche: dirlo è
    // più utile che mostrare uno zero, che si leggerebbe come "nessun core".
    return <span className="text-muted">{t('non dichiarata')}</span>
  }
  // La memoria **della macchina**, non quella in uso dal processo: quest'ultima
  // a riposo è sotto il megabyte, e accanto a "4 core" un "0 MB" si legge come
  // una macchina senza memoria invece che come un processo che non ne consuma.
  const host = (metrics.host ?? {}) as Record<string, unknown>
  const memory = typeof host.memory_mb === 'number' ? host.memory_mb : null
  const platform = typeof metrics.platform === 'string' ? metrics.platform : null
  return (
    <>
      <span className="chip">{t('{n} core', { n: cores })}</span>
      {memory !== null && <span className="chip">{formatMB(memory)} RAM</span>}
      {platform && <span className="chip">{platform}</span>}
    </>
  )
}

function formatMB(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`
}
